import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { FileText, Save, Loader2, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteSettings = () => {
    const { token } = useAuth();
    const fileInputRef = useRef(null);
    const [settings, setSettings] = useState({
        quote_company_name: '',
        quote_company_tagline: '',
        quote_footer_line1: '',
        quote_footer_line2: '',
        quote_footer_line3: ''
    });
    const [logoPreview, setLogoPreview] = useState(null);
    const [hasLogo, setHasLogo] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    useEffect(() => {
        fetchSettings();
        fetchLogo();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API}/admin/settings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSettings({
                quote_company_name: response.data.quote_company_name || 'ADVANCED DYNAMICS',
                quote_company_tagline: response.data.quote_company_tagline || 'Packaging Machinery Solutions',
                quote_footer_line1: response.data.quote_footer_line1 || 'Advanced Dynamics Ltd',
                quote_footer_line2: response.data.quote_footer_line2 || 'Tel: 01274 731222 | www.advanceddynamics.co.uk',
                quote_footer_line3: response.data.quote_footer_line3 || 'This quote is valid for 30 days from the date of issue.'
            });
            setHasLogo(!!response.data.logo_path);
        } catch (error) {
            toast.error('Failed to load quote settings');
        } finally {
            setLoading(false);
        }
    };

    const fetchLogo = async () => {
        try {
            const response = await axios.get(`${API}/admin/settings/logo`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
                validateStatus: (status) => status < 500 // Accept 204 as valid
            });
            
            if (response.status === 200 && response.data.size > 0) {
                const url = URL.createObjectURL(response.data);
                setLogoPreview(url);
                setHasLogo(true);
            } else {
                // No logo (204 response or empty)
                setHasLogo(false);
                setLogoPreview(null);
            }
        } catch (error) {
            // Error fetching logo - this is fine
            setHasLogo(false);
            setLogoPreview(null);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            await axios.put(
                `${API}/admin/settings`,
                settings,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Quote settings saved successfully');
        } catch (error) {
            toast.error('Failed to save quote settings');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type. Please upload PNG, JPEG, or GIF');
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 2MB');
            return;
        }

        setUploadingLogo(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            await axios.post(`${API}/admin/settings/logo`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            // Preview the uploaded logo
            const url = URL.createObjectURL(file);
            setLogoPreview(url);
            setHasLogo(true);
            toast.success('Logo uploaded successfully');
        } catch (error) {
            toast.error('Failed to upload logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleDeleteLogo = async () => {
        try {
            await axios.delete(`${API}/admin/settings/logo`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLogoPreview(null);
            setHasLogo(false);
            toast.success('Logo deleted successfully');
        } catch (error) {
            toast.error('Failed to delete logo');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-brand-lime" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto animate-fade-in space-y-6">
            <div>
                <h1 className="text-3xl font-bold font-heading text-brand-charcoal">
                    Quote Settings
                </h1>
                <p className="text-brand-gray-500 mt-1">
                    Customize how your PDF quotes look
                </p>
            </div>

            {/* Logo Upload */}
            <Card className="border-0 shadow-lg">
                <CardHeader>
                    <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
                        <ImageIcon className="h-5 w-5 text-brand-lime" />
                        Company Logo
                    </CardTitle>
                    <CardDescription>
                        Upload your company logo to appear on PDF quotes (PNG, JPEG, or GIF, max 2MB)
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                        {/* Logo Preview */}
                        <div className="w-full sm:w-48 h-24 bg-brand-gray-100 border-2 border-dashed border-brand-gray-300 flex items-center justify-center overflow-hidden">
                            {logoPreview ? (
                                <img 
                                    src={logoPreview} 
                                    alt="Company Logo" 
                                    className="max-w-full max-h-full object-contain"
                                />
                            ) : (
                                <div className="text-center text-brand-gray-400">
                                    <ImageIcon className="h-8 w-8 mx-auto mb-1" />
                                    <span className="text-xs">No logo</span>
                                </div>
                            )}
                        </div>

                        {/* Upload Controls */}
                        <div className="flex-1 space-y-3">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleLogoUpload}
                                accept="image/png,image/jpeg,image/jpg,image/gif"
                                className="hidden"
                                data-testid="logo-file-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingLogo}
                                className="w-full"
                                data-testid="upload-logo-btn"
                            >
                                {uploadingLogo ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                    <Upload className="h-4 w-4 mr-2" />
                                )}
                                {hasLogo ? 'Replace Logo' : 'Upload Logo'}
                            </Button>
                            {hasLogo && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleDeleteLogo}
                                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid="delete-logo-btn"
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Remove Logo
                                </Button>
                            )}
                            <p className="text-xs text-brand-gray-400">
                                Recommended: 200x75 pixels, transparent background
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Quote Header Settings */}
            <form onSubmit={handleSave}>
                <Card className="border-0 shadow-lg">
                    <CardHeader>
                        <CardTitle className="text-xl font-bold font-heading flex items-center gap-2">
                            <FileText className="h-5 w-5 text-brand-lime" />
                            Quote Header
                        </CardTitle>
                        <CardDescription>
                            Company name and tagline shown at the top of quotes
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="company_name" className="text-xs font-bold uppercase tracking-wide">
                                Company Name
                            </Label>
                            <Input
                                id="company_name"
                                value={settings.quote_company_name}
                                onChange={(e) => setSettings({ ...settings, quote_company_name: e.target.value })}
                                placeholder="ADVANCED DYNAMICS"
                                className="h-11"
                                data-testid="quote-company-name"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="tagline" className="text-xs font-bold uppercase tracking-wide">
                                Tagline
                            </Label>
                            <Input
                                id="tagline"
                                value={settings.quote_company_tagline}
                                onChange={(e) => setSettings({ ...settings, quote_company_tagline: e.target.value })}
                                placeholder="Packaging Machinery Solutions"
                                className="h-11"
                                data-testid="quote-tagline"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Quote Footer Settings */}
                <Card className="border-0 shadow-lg mt-6">
                    <CardHeader>
                        <CardTitle className="text-xl font-bold font-heading">
                            Quote Footer
                        </CardTitle>
                        <CardDescription>
                            Text shown at the bottom of every quote PDF
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="footer1" className="text-xs font-bold uppercase tracking-wide">
                                Footer Line 1
                            </Label>
                            <Input
                                id="footer1"
                                value={settings.quote_footer_line1}
                                onChange={(e) => setSettings({ ...settings, quote_footer_line1: e.target.value })}
                                placeholder="Advanced Dynamics Ltd"
                                className="h-11"
                                data-testid="quote-footer-line1"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="footer2" className="text-xs font-bold uppercase tracking-wide">
                                Footer Line 2
                            </Label>
                            <Input
                                id="footer2"
                                value={settings.quote_footer_line2}
                                onChange={(e) => setSettings({ ...settings, quote_footer_line2: e.target.value })}
                                placeholder="Tel: 01274 731222 | www.advanceddynamics.co.uk"
                                className="h-11"
                                data-testid="quote-footer-line2"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="footer3" className="text-xs font-bold uppercase tracking-wide">
                                Footer Line 3 (Validity Text)
                            </Label>
                            <Input
                                id="footer3"
                                value={settings.quote_footer_line3}
                                onChange={(e) => setSettings({ ...settings, quote_footer_line3: e.target.value })}
                                placeholder="This quote is valid for 30 days from the date of issue."
                                className="h-11"
                                data-testid="quote-footer-line3"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Preview Info */}
                <Card className="border-0 shadow-lg mt-6 bg-brand-gray-50">
                    <CardContent className="py-4">
                        <p className="text-sm text-brand-gray-600">
                            <strong>Note:</strong> Changes will apply to all new quotes. Existing quote PDFs will not be affected.
                        </p>
                    </CardContent>
                </Card>

                <div className="flex justify-end pt-6">
                    <Button
                        type="submit"
                        className="h-12 px-8 bg-[#7AC943] hover:bg-[#68B035] text-black font-bold uppercase tracking-wide"
                        disabled={saving}
                        data-testid="save-quote-settings-btn"
                    >
                        {saving ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                            <>
                                <Save className="h-5 w-5 mr-2" />
                                Save Settings
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default QuoteSettings;
